import { useState, useRef, type ChangeEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Icon } from './Icon.tsx';
import { api } from '../lib/api.ts';
import { t } from '../lib/strings.ts';

interface SmsTemplateDto {
  kind: string;
  body: string | null;
  defaultBody: string;
  tokens: string[];
  sample: string;
}

// Sample values for the live preview — mirrors the server's SAMPLE_TOKENS.
const SAMPLE: Record<string, string> = {
  council: 'Council ge nan',
  ref: '12',
  gsrNumber: 'MG/PC01/2026/12',
  deadline: '20 June 2026 14:00',
  requiredDate: '25 June 2026',
  deliverBy: '25 June 2026',
  vendor: 'Vendor ge nan',
  url: 'https://pro.wapplify.app/q/AbCd1234',
};

function renderTokens(body: string): string {
  return body.replace(/\{(\w+)\}/g, (_m, k: string) => SAMPLE[k] ?? '');
}

function MessageEditor({ tpl }: { tpl: SmsTemplateDto }): JSX.Element {
  const qc = useQueryClient();
  const [value, setValue] = useState(tpl.body ?? '');
  const [saved, setSaved] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);

  const save = useMutation({
    mutationFn: (body: string | null) =>
      api.put(`/org-settings/sms/templates/${tpl.kind}`, { body }).then((r) => r.data),
    onSuccess: () => {
      setSaved(true);
      void qc.invalidateQueries({ queryKey: ['sms-templates'] });
      setTimeout(() => setSaved(false), 2500);
    },
  });

  // Insert a {token} at the caret (or append) and keep focus.
  function insertToken(token: string) {
    const el = ref.current;
    const chip = `{${token}}`;
    if (!el) { setValue((v) => v + chip); return; }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const next = value.slice(0, start) + chip + value.slice(end);
    setValue(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + chip.length;
      el.setSelectionRange(pos, pos);
    });
  }

  const isDefault = value.trim() === '';
  const preview = renderTokens(isDefault ? tpl.defaultBody : value);

  return (
    <div className="rounded-lg border border-border p-3 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-medium text-ink">{t.smsMessages.kind[tpl.kind] ?? tpl.kind}</h3>
        {isDefault && <span className="pill pill-neutral">{t.smsMessages.usingDefault}</span>}
      </div>

      {/* Token chips */}
      <div className="flex flex-col gap-1">
        <span className="status-label text-muted-foreground">{t.smsMessages.tokensLabel}</span>
        <div className="flex flex-wrap gap-1.5">
          {tpl.tokens.map((tok) => (
            <button
              key={tok}
              type="button"
              onClick={() => insertToken(tok)}
              className="btn-secondary btn-sm gap-1"
              title={t.smsMessages.token[tok] ?? tok}
            >
              <span className="num" dir="ltr">{`{${tok}}`}</span>
              <span className="text-muted-foreground">{t.smsMessages.token[tok] ?? ''}</span>
            </button>
          ))}
        </div>
      </div>

      <textarea
        ref={ref}
        dir="ltr"
        rows={4}
        className="textarea w-full font-mono text-sm"
        value={value}
        placeholder={tpl.defaultBody}
        onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setValue(e.target.value)}
      />

      {/* Live preview */}
      <div className="rounded-md bg-muted/40 p-2">
        <span className="status-label text-muted-foreground">{t.smsMessages.previewLabel}</span>
        <p dir="ltr" className="mt-0.5 whitespace-pre-wrap text-sm text-foreground">{preview}</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => save.mutate(value.trim() === '' ? null : value)} disabled={save.isPending} className="btn-primary btn-sm">
          {save.isPending ? t.smsMessages.saving : t.smsMessages.save}
        </button>
        {!isDefault && (
          <button type="button" onClick={() => { setValue(''); save.mutate(null); }} disabled={save.isPending} className="btn-ghost btn-sm gap-1">
            <Icon name="restart_alt" size={14} />
            {t.smsMessages.reset}
          </button>
        )}
        {saved && <span className="text-sm text-success">{t.smsMessages.saved}</span>}
        {save.isError && <span className="text-sm text-destructive">{t.smsMessages.saveFailed}</span>}
      </div>
    </div>
  );
}

export function SmsMessagesSection(): JSX.Element {
  const { data, isLoading } = useQuery({
    queryKey: ['sms-templates'],
    queryFn: async () => (await api.get<{ templates: SmsTemplateDto[] }>('/org-settings/sms/templates')).data.templates,
  });

  return (
    <section className="card-paper p-5 space-y-3">
      <div>
        <h2 className="font-semibold text-ink">{t.smsMessages.heading}</h2>
        <p className="mt-0.5 text-sm text-muted-foreground">{t.smsMessages.intro}</p>
      </div>
      {isLoading || !data ? (
        <p className="text-sm text-muted-foreground">{t.smsMessages.loading}</p>
      ) : (
        <div className="space-y-3">
          {data.map((tpl) => (
            <MessageEditor key={tpl.kind} tpl={tpl} />
          ))}
        </div>
      )}
    </section>
  );
}
